import React from 'react';
import { formatCurrency, formatDate } from '@/utils/formatters';
// import { getChangeColor } from '@/utils/formatters'; // Unused for now

interface Trade {
  id: string;
  symbol: string;
  type: 'buy' | 'sell';
  quantity: number;
  price: number;
  total: number;
  timestamp: string;
  status: 'completed' | 'pending' | 'failed';
}

interface RecentTradesTableProps {
  trades?: Trade[];
  maxRows?: number;
}

const RecentTradesTable: React.FC<RecentTradesTableProps> = ({ trades, maxRows = 5 }) => {
  // Mock data for demonstration
  const mockTrades: Trade[] = trades || [
    {
      id: '1',
      symbol: 'BTC/USDT',
      type: 'buy',
      quantity: 0.025,
      price: 43250.00,
      total: 1081.25,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
      status: 'completed'
    },
    {
      id: '2',
      symbol: 'ETH/USDT',
      type: 'sell',
      quantity: 0.5,
      price: 2680.50,
      total: 1340.25,
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
      status: 'completed'
    },
    {
      id: '3',
      symbol: 'ADA/USDT',
      type: 'buy',
      quantity: 1000,
      price: 0.485,
      total: 485.00,
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      status: 'completed'
    },
    {
      id: '4',
      symbol: 'SOL/USDT',
      type: 'buy',
      quantity: 10,
      price: 102.30,
      total: 1023.00,
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
      status: 'pending'
    },
    {
      id: '5',
      symbol: 'MATIC/USDT',
      type: 'sell',
      quantity: 500,
      price: 0.89,
      total: 445.00,
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
      status: 'completed'
    },
  ];

  const displayTrades = mockTrades.slice(0, maxRows);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Completed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'buy' ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Recent Trades</h3>
        <p className="text-sm text-gray-500">Latest trading activity</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pair
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayTrades.map((trade) => (
              <tr key={trade.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{trade.symbol}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-sm font-medium capitalize ${getTypeColor(trade.type)}`}>
                    {trade.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {trade.quantity.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(trade.price)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {formatCurrency(trade.total)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(trade.timestamp)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(trade.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Footer with view all link */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {displayTrades.length} of {mockTrades.length} recent trades
          </p>
          <button className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            View all trades →
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecentTradesTable; 